import { Component, Input, output, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { ToastService } from '../toast/toast';
import { AdminPanelComponent } from './admin-panel.component';

/**
 * Admin-only debug component for unit pages.
 * Collapsed by default into a subtle toggle bar.
 * Expands to show unit tags, admin links, and optional validation results.
 */
@Component({
    standalone: true,
    selector: 'abs-admin-unit-debug',
    imports: [CommonModule, RouterModule, IonicModule, AdminPanelComponent],
    template: `
        <abs-admin-panel label="Admin">
            <div class="admin-panel-actions">
                <a class="admin-panel-btn" target="_blank" [routerLink]="['/admin', unitGameId, 'entity', 'units', unitId]">
                    <ion-icon name="create-outline"></ion-icon>
                    Edit Unit
                </a>
                @if (validateUnit) {
                    <button class="admin-panel-btn" (click)="validateUnit.emit()">
                        <ion-icon name="checkmark-circle-outline"></ion-icon>
                        Validate
                    </button>
                }
            </div>

            @if (tags?.length) {
                <div class="admin-panel-tags">
                    @for (tag of tags; track tag) {
                        <span class="admin-panel-tag">{{ tag }}</span>
                    }
                </div>
            }

            @if (validationResults) {
                <pre class="admin-panel-pre" (click)="copyToClipboard()">{{ validationResults | json }}</pre>
            }
        </abs-admin-panel>
    `,
    styles: [
        `
            /*
             * These belong here rather than in abs-admin-panel, which owns the same class names.
             * That component uses ViewEncapsulation.ShadowDom and projects through a raw <slot>,
             * so its styles cannot reach this component's markup — shadow styles only apply to
             * slotted content via ::slotted(), which matches top-level nodes and so cannot reach
             * buttons nested inside .admin-panel-actions.
             *
             * Left unstyled, the app themes win instead: Bolt Action's '.bolt-action button'
             * paints every button white with the Univers uppercase treatment, which rendered the
             * Validate button as white text on white with no border. The Edit link escaped only
             * because it is an <a>, which that rule does not target.
             *
             * Two class selectors, so this beats '.bolt-action button' at (0,1,1).
             */
            .admin-panel-actions {
                display: flex;
                flex-wrap: wrap;
                gap: 6px;
            }

            .admin-panel-actions .admin-panel-btn {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                padding: 4px 10px;
                border-radius: 4px;
                border: 1px solid rgba(0, 0, 0, 0.12);
                background: #fff;
                color: #555;
                font-family: inherit;
                font-size: 0.95em;
                font-weight: 500;
                text-transform: none;
                text-decoration: none;
                cursor: pointer;
            }

            .admin-panel-actions .admin-panel-btn:hover {
                background: rgba(0, 0, 0, 0.05);
                color: #555;
            }

            .admin-panel-actions .admin-panel-btn ion-icon {
                font-size: 1.1em;
            }
        `
    ],
    encapsulation: ViewEncapsulation.None
})
export class AdminUnitDebugComponent {
    /** Tags associated with the unit (for display purposes) */
    @Input() tags: string[] = [];

    /** The game ID for routing to admin panel */
    @Input() gameId!: string;

    /** The unit's template game ID (may differ from force game ID for merged games) */
    @Input() unitGameId!: string;

    /** The unit template ID for routing to admin panel */
    @Input() unitId!: string;

    /** Validation results to display (game-specific format) */
    @Input() validationResults: any;

    @Input() canValidate = false;

    /** Emitted when the validate button is clicked - handled by parent component */
    validateUnit = output<void>();

    constructor(private toast: ToastService) {}

    copyToClipboard() {
        const text = JSON.stringify(this.validationResults, null, 4);
        navigator.clipboard
            .writeText(text)
            .then(() => {
                this.toast.showToast('', 'Copied to clipboard', { color: 'success' });
            })
            .catch((err) => {
                this.toast.showToast('', 'Error copying to clipboard', { color: 'danger' });
            });
    }
}
